import React from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { AttachMoney, DirectionsCar, TrendingDown, TrendingUp } from '@mui/icons-material';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { reportApi } from '../../services/api';
import { formatCurrency } from '../../utils/formatters';
import { ReportDateRangeFilter } from '../../components/reports/ReportDateRangeFilter';
import { ReportPieChart } from '../../components/reports/ReportPieChart';
import { useReportDateRange } from '../../hooks/useReportDateRange';
import { getQueryErrorMessage } from '../../utils/queryError';

export const VehiclePerformanceReportPage: React.FC = () => {
  const { t } = useTranslation();
  const { range, setRange, minimumStartDate } = useReportDateRange();

  const { data: report, error, isError, isLoading } = useQuery(
    ['vehiclePerformanceReport', range.startDate, range.endDate],
    () => reportApi.getVehiclePerformance(range.startDate, range.endDate)
  );

  const profitPieData = (report?.vehicles ?? [])
    .filter((vehicle) => vehicle.profit > 0)
    .map((vehicle) => ({ name: vehicle.vehicleName, value: vehicle.profit }));

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t('reports.vehiclePerformance.title', 'Vehicle Performance Report')}
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        {t(
          'reports.vehiclePerformance.subtitle',
          'Revenue, vehicle-linked maintenance and compliance costs, and profit per vehicle'
        )}
      </Typography>

      <ReportDateRangeFilter
        value={range}
        onChange={setRange}
        minimumStartDate={minimumStartDate}
      />

      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Alert severity="error">
          {getQueryErrorMessage(
            error,
            t(
              'reports.vehiclePerformance.loadError',
              'Unable to load the vehicle performance report for that date range.'
            )
          )}
        </Alert>
      ) : (
        <>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <TrendingUp color="success" />
                    <Typography variant="body2" color="text.secondary">
                      {t('reports.vehiclePerformance.totalRevenue', 'Fleet Revenue')}
                    </Typography>
                  </Box>
                  <Typography variant="h4" color="success.main">
                    {formatCurrency(report?.totalRevenue)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <TrendingDown color="error" />
                    <Typography variant="body2" color="text.secondary">
                      {t('reports.vehiclePerformance.totalExpenses', 'Fleet Operational Costs')}
                    </Typography>
                  </Box>
                  <Typography variant="h4" color="error.main">
                    {formatCurrency(report?.totalExpenses)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <AttachMoney color="primary" />
                    <Typography variant="body2" color="text.secondary">
                      {t('reports.vehiclePerformance.totalProfit', 'Fleet Operational Profit')}
                    </Typography>
                  </Box>
                  <Typography
                    variant="h4"
                    color={(report?.totalProfit ?? 0) >= 0 ? 'success.main' : 'error.main'}
                  >
                    {formatCurrency(report?.totalProfit)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Alert severity="info" icon={<DirectionsCar fontSize="small" />} sx={{ mb: 3 }}>
            {t(
              'reports.vehiclePerformance.caveat',
              'Operational costs cover maintenance, tax, insurance, inspections, service completions, and vehicle history events linked to each vehicle. General overhead (salaries, rent, utilities) is not allocated per vehicle here - see the Total Profit Report for company-wide net profit.'
            )}
          </Alert>

          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('reports.vehiclePerformance.tableTitle', 'Per-Vehicle Breakdown')}
                  </Typography>
                  {!report || report.vehicles.length === 0 ? (
                    <Alert severity="info">{t('reports.vehiclePerformance.empty', 'No vehicles found')}</Alert>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>{t('reports.vehiclePerformance.vehicle', 'Vehicle')}</TableCell>
                            <TableCell align="right">{t('reports.vehiclePerformance.reservations', 'Rentals')}</TableCell>
                            <TableCell align="right">{t('reports.vehiclePerformance.revenue', 'Revenue')}</TableCell>
                            <TableCell align="right">{t('reports.vehiclePerformance.expenses', 'Costs')}</TableCell>
                            <TableCell align="right">{t('reports.vehiclePerformance.profit', 'Profit')}</TableCell>
                            <TableCell align="right">{t('reports.vehiclePerformance.margin', 'Margin')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {report.vehicles.map((vehicle) => (
                            <TableRow key={vehicle.vehicleId}>
                              <TableCell>
                                <Typography variant="body2">{vehicle.vehicleName}</Typography>
                                <Chip label={vehicle.licensePlate} size="small" variant="outlined" />
                              </TableCell>
                              <TableCell align="right">{vehicle.reservationCount}</TableCell>
                              <TableCell align="right">{formatCurrency(vehicle.revenue)}</TableCell>
                              <TableCell align="right">{formatCurrency(vehicle.totalExpenses)}</TableCell>
                              <TableCell
                                align="right"
                                sx={{ color: vehicle.profit >= 0 ? 'success.main' : 'error.main' }}
                              >
                                {formatCurrency(vehicle.profit)}
                              </TableCell>
                              <TableCell align="right">{vehicle.profitMarginPercent.toFixed(1)}%</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={5}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('reports.vehiclePerformance.profitShare', 'Profit Share by Vehicle')}
                  </Typography>
                  <ReportPieChart
                    data={profitPieData}
                    emptyLabel={t('reports.vehiclePerformance.empty', 'No vehicles found')}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}
    </Container>
  );
};
